import { useEffect, useRef } from "react";
import { toast } from "sonner";
import { claimNotificationToast } from "../services/notifications";

const rowsById = data => new Map([...(data?.items || []), ...(data?.unread_items || [])].map(row => [row.id, row]));

/** Consume confirmed inbox reads only; no extra polling or booking mutations. */
export default function useNotificationToasts(resource, onView, onFailure, configuration) {
  const { kind, isEnabled, eligible } = configuration;
  const enabled = isEnabled(resource);
  const preferencesChangedAt = configuration.preferencesChangedAt?.(resource);
  const latest = useRef(null);
  latest.current = { resource, onView, onFailure };
  const active = useRef(true);
  const tracker = useRef({ initialized: false, since: 0, seen: new Set(), toasts: new Map(), opening: new Set(), claims: new Map() });
  useEffect(() => {
    active.current = true;
    const current = tracker.current;
    return () => {
      active.current = false;
      current.toasts.forEach(id => toast.dismiss(id));
      current.toasts.clear();
      current.claims.forEach(controller => controller.abort());
      current.claims.clear();
    };
  }, []);
  useEffect(() => {
    const current = tracker.current;
    const rows = rowsById(resource.data);
    const dismiss = identifier => {
      const toastId = current.toasts.get(identifier);
      if (toastId) toast.dismiss(toastId);
      current.toasts.delete(identifier);
    };
    // An access failure clears all this inbox's toasts, including stale actions.
    current.toasts.forEach((_, identifier) => {
      const row = rows.get(identifier);
      if (!enabled || resource.error || !row || row.read_at || row.kind !== kind || !eligible(row)) dismiss(identifier);
    });
    if (!enabled) {
      current.claims.forEach(controller => controller.abort()); current.claims.clear();
    }
    if (resource.loading || resource.error) return;
    if (!current.initialized || !enabled || current.enabled === false) {
      current.initialized = true;
      current.enabled = enabled;
      if (!configuration.includeBacklog) {
        current.since = Math.max(current.since, Number.isFinite(Date.parse(resource.data.as_of)) ? Date.parse(resource.data.as_of) : Date.now());
        rows.forEach(row => current.seen.add(row.id));
        return; // Booking-notice first loads and re-enabling retain their quiet behavior.
      }
    }
    // A sleeping tab or different origin may miss both off/on transitions.
    // The server's last preference change still excludes the muted backlog.
    const changedAt = Date.parse(preferencesChangedAt);
    if (Number.isFinite(changedAt)) current.since = Math.max(current.since, changedAt);
    rows.forEach(notice => {
      if (current.seen.has(notice.id)) return;
      if (notice.kind !== kind || !eligible(notice) || notice.read_at || Date.parse(notice.created_at) <= current.since
          || !Number.isFinite(Date.parse(notice.created_at))) { current.seen.add(notice.id); return; }
      // Only a visible tab competes for the durable per-account presentation slot.
      if (document.visibilityState === "hidden") return;
      current.seen.add(notice.id);
      const controller = new AbortController(); current.claims.set(notice.id, controller);
      const present = async () => {
        try {
          const claim = await claimNotificationToast(notice.id, controller.signal);
          const state = latest.current, row = rowsById(state.resource.data).get(notice.id);
          if (!active.current || controller.signal.aborted || !isEnabled(state.resource) || state.resource.loading || state.resource.error
              || !row || row.read_at || row.kind !== kind || !eligible(row) || claim?.ok !== true || claim.claimed !== true) return;
          const id = `${configuration.prefix}:${notice.id}`;
          current.toasts.set(notice.id, id);
          const clear = () => current.toasts.delete(notice.id);
          toast.info(configuration.title(row), {
            id, duration: 12000,
            closeButton: true,
            classNames: {
              toast: "!pr-10",
              closeButton: "!left-auto !right-2 !top-2 !h-6 !w-6 !transform-none",
            },
            description: configuration.description(row),
            onDismiss: clear, onAutoClose: clear,
            action: {
              label: configuration.actionLabel,
              onClick: async event => {
                event.preventDefault(); // Keep the toast until its read status is confirmed.
                const state = latest.current;
                const row = rowsById(state.resource.data).get(notice.id);
                if (!active.current || !isEnabled(state.resource) || state.resource.loading || state.resource.error || !row
                    || row.kind !== kind || !eligible(row) || current.opening.has(notice.id)) return;
                current.opening.add(notice.id);
                try {
                  const opened = await state.onView(row);
                  if (active.current) {
                    if (opened) dismiss(notice.id);
                    else latest.current.onFailure();
                  }
                } catch {
                  if (active.current) latest.current.onFailure();
                } finally {
                  current.opening.delete(notice.id);
                }
              },
            },
          });
        } catch { /* An uncertain claim cannot authorize another toast. The inbox remains available. */ }
        finally { current.claims.delete(notice.id); }
      };
      present();
    });
  }, [resource.data, resource.loading, resource.error, enabled, configuration, kind, isEnabled, eligible, preferencesChangedAt]);
}
